'use client';

import * as React from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';

import { isRTL } from '@/lib/i18n';
import { useLocale } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

const Accordion = ({
  dir,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) => {
  const localeDir = isRTL(useLocale()) ? 'rtl' : 'ltr';
  return <AccordionPrimitive.Root dir={dir ?? localeDir} {...props} />;
};

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline ltr:text-left rtl:text-right [&[data-state=open]>svg]:rotate-180',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown className="text-muted-foreground size-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    // `forceMount` keeps the answer in the served HTML even while collapsed.
    // Without it Radix unmounts closed content, so every FAQ on the site was
    // shipping FAQPage structured data whose answers appeared nowhere on the
    // page — markup that does not match the page is a policy problem, and
    // answer engines reading the markup found nothing to corroborate it.
    //
    // Radix marks the closed state with the `hidden` attribute, which would
    // `display: none` the element and kill the open/close animation. So the
    // attribute's effect is overridden and the collapse is done by height
    // instead: still invisible, still zero-height, but present in the DOM and
    // readable by a crawler. This is the pattern Google documents as fine —
    // content behind expandable UI is indexed as long as it is in the HTML.
    forceMount
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm [&[hidden]]:block data-[state=closed]:h-0"
    {...props}
  >
    <div className={cn('pt-0 pb-4', className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
